<template>
    <section :class="['customer-loyalty-dashboard', { dark: isDark }]">
        <header class="dashboard-header">
            <div>
                <p class="eyebrow">Focused dashboard</p>
                <h1>Customer &amp; Loyalty</h1>
                <p class="subtitle">Customer identification, repeat behaviour, retention, consent, and loyalty adoption from paid local sales.</p>
            </div>
            <router-link class="header-link" to="/reports/customer">Customer report <i class="material-icons" aria-hidden="true">arrow_forward</i></router-link>
        </header>

        <section class="control-bar" aria-label="Customer and loyalty filters">
            <label>
                <span>Store</span>
                <select v-model="warehouseId" :disabled="loading" @change="fetchDashboard">
                    <option value="">All locations</option>
                    <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">{{ warehouse.name }}</option>
                </select>
            </label>
            <label>
                <span>Period</span>
                <select v-model="period" :disabled="loading" @change="changePeriod">
                    <option value="last7days">Last 7 days</option>
                    <option value="last30days">Last 30 days</option>
                    <option value="last90days">Last 90 days</option>
                    <option value="last180days">Last 180 days</option>
                </select>
            </label>
            <label>
                <span>Trend interval</span>
                <select v-model="interval" :disabled="loading" @change="fetchDashboard">
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                </select>
            </label>
            <label v-if="user && user.isAdmin" class="pilot-toggle">
                <input v-model="includePilotSales" type="checkbox" :disabled="loading" @change="fetchDashboard">
                <span>Include pilot/test sales</span>
            </label>
        </section>

        <div v-if="!canViewDashboard" class="state-panel permission-state">
            <i class="material-icons" aria-hidden="true">lock</i>
            <div>
                <h2>Customer and loyalty access required</h2>
                <p>Customer report, sales report, and loyalty view permissions are required because this dashboard combines all three sources.</p>
            </div>
        </div>
        <div v-else-if="loading" class="state-panel loading-state" role="status" aria-live="polite">
            <i class="material-icons" aria-hidden="true">sync</i>
            <div><h2>Loading customer behaviour…</h2><p>Joining paid sales to customer profiles and loyalty accounts.</p></div>
        </div>
        <div v-else-if="error" class="state-panel error-state" role="alert">
            <i class="material-icons" aria-hidden="true">error_outline</i>
            <div><h2>Customer analytics could not load</h2><p>{{ error }}</p><button type="button" @click="fetchDashboard">Try again</button></div>
        </div>
        <template v-else>
            <aside :class="['coverage-banner', summary.identificationRate < 50 ? 'warning' : 'neutral']">
                <i class="material-icons" aria-hidden="true">badge</i>
                <div>
                    <strong>{{ formatPercent(summary.identificationRate) }} of paid orders have a matched customer</strong>
                    <p>Repeat, value, consent, and loyalty metrics describe the identified subset only. Anonymous orders remain visible in the denominator.</p>
                </div>
            </aside>

            <section class="metric-grid" aria-label="Customer and loyalty summary">
                <article v-for="card in summaryCards" :key="card.label" :class="['metric-card', card.tone]">
                    <i class="material-icons" aria-hidden="true">{{ card.icon }}</i>
                    <div><span>{{ card.label }}</span><strong>{{ card.value }}</strong><small>{{ card.helper }}</small></div>
                </article>
            </section>

            <section class="chart-grid">
                <article class="dashboard-card chart-card wide">
                    <div class="section-heading">
                        <div><p class="eyebrow">Customer mix</p><h2>New vs returning customers</h2><p>Each identified customer is counted once in their first activity bucket for the selected period.</p></div>
                        <span class="source-badge">Observed invoice history</span>
                    </div>
                    <div v-if="mixLabels.length" class="chart-frame trend-chart-frame">
                        <EChartsStackedBarChart
                            :labels="mixLabels"
                            :series="mixSeries"
                            :is-dark="isDark"
                            aria-label="New and returning identified customers by period"
                        />
                    </div>
                    <div v-else class="empty-chart">No identified customer activity in this period.</div>
                </article>

                <article class="dashboard-card chart-card wide">
                    <div class="section-heading">
                        <div><p class="eyebrow">Cohort retention</p><h2>Monthly customer retention</h2><p>First-purchase cohorts acquired inside the selected window; each cell shows the share purchasing again in that later month.</p></div>
                        <span class="source-badge">{{ formatInteger(cohortRetention.customerCount) }} acquired customers</span>
                    </div>
                    <div v-if="cohortRetention.cohorts && cohortRetention.cohorts.length" class="chart-frame retention-chart-frame">
                        <EChartsRetentionHeatmap
                            :cohorts="cohortRetention.cohorts"
                            :periods="cohortRetention.periods || []"
                            :is-dark="isDark"
                            aria-label="Monthly retention of identified customer acquisition cohorts"
                        />
                    </div>
                    <div v-else class="empty-chart">No newly acquired identified customers are available for cohort analysis in this period.</div>
                </article>

                <article class="dashboard-card chart-card">
                    <div class="section-heading"><div><p class="eyebrow">Customer value</p><h2>Spend bands</h2><p>Selected-period revenue per identified customer.</p></div></div>
                    <div class="chart-frame">
                        <EChartsHorizontalBarChart
                            :categories="valueBands.map(item => item.label)"
                            :values="valueBands.map(item => item.customers)"
                            :is-dark="isDark"
                            series-name="Customers"
                            aria-label="Identified customers by selected-period spend band"
                            color="#0d2d2c"
                            dark-color="#4ade80"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>

                <article class="dashboard-card chart-card">
                    <div class="section-heading"><div><p class="eyebrow">Purchase depth</p><h2>Orders per customer</h2><p>Frequency distribution within the selected period.</p></div></div>
                    <div class="chart-frame">
                        <EChartsHorizontalBarChart
                            :categories="frequencyBands.map(item => item.label)"
                            :values="frequencyBands.map(item => item.customers)"
                            :is-dark="isDark"
                            series-name="Customers"
                            aria-label="Identified customer count by order frequency"
                            color="#b8843f"
                            dark-color="#e7bd78"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>

                <article class="dashboard-card chart-card">
                    <div class="section-heading"><div><p class="eyebrow">Engagement coverage</p><h2>Identity to loyalty</h2><p>Matched customers, marketing consent, and active loyalty accounts.</p></div></div>
                    <div class="chart-frame">
                        <EChartsHorizontalBarChart
                            :categories="engagementFunnel.map(item => item.label)"
                            :values="engagementFunnel.map(item => item.customers)"
                            :is-dark="isDark"
                            series-name="Customers"
                            aria-label="Customer identification marketing opt-in and loyalty enrollment funnel"
                            color="#347a63"
                            dark-color="#86d5b4"
                            :value-formatter="formatInteger"
                        />
                    </div>
                </article>

                <article class="dashboard-card definition-card">
                    <div class="section-heading"><div><p class="eyebrow">Definitions</p><h2>What the metrics mean</h2></div></div>
                    <dl>
                        <div><dt>Identified customer</dt><dd>{{ context.identityDefinition }}</dd></div>
                        <div><dt>New customer</dt><dd>{{ context.newCustomerDefinition }}</dd></div>
                        <div><dt>Repeat customer</dt><dd>{{ context.repeatCustomerDefinition }}</dd></div>
                        <div><dt>Cohort retention</dt><dd>{{ context.cohortRetentionDefinition }}</dd></div>
                    </dl>
                </article>
            </section>

            <section class="dashboard-card table-card">
                <div class="section-heading">
                    <div><p class="eyebrow">Customer detail</p><h2>Highest-value identified customers</h2><p>Selected-period revenue; use the customer record for operational follow-up.</p></div>
                    <router-link class="mini-link" to="/reports/customer">Customer report</router-link>
                </div>
                <div class="table-scroll">
                    <table>
                        <thead><tr><th>Customer</th><th>Orders</th><th>Revenue</th><th>Last purchase</th><th>Marketing</th><th>Loyalty</th><th></th></tr></thead>
                        <tbody>
                            <tr v-for="customer in topCustomers" :key="customer.customerId">
                                <td><strong>{{ customer.customerName }}</strong><small>{{ customer.customerId }}</small></td>
                                <td>{{ formatInteger(customer.orders) }}</td>
                                <td>{{ formatCurrency(customer.revenue) }}</td>
                                <td>{{ formatShortDate(customer.lastPurchaseDate) }}</td>
                                <td><span :class="['status-pill', customer.marketingOptIn ? 'success' : 'muted']">{{ customer.marketingOptIn ? "Opted in" : "Not opted in" }}</span></td>
                                <td><span :class="['status-pill', customer.loyaltyEnrolled ? 'success' : 'muted']">{{ customer.loyaltyEnrolled ? (customer.loyaltyTier || "Enrolled") : "Not enrolled" }}</span></td>
                                <td><router-link class="row-link" :to="`/customer-loyalty/${customer.customerId}`">View</router-link></td>
                            </tr>
                            <tr v-if="!topCustomers.length"><td colspan="7" class="empty-row">No matched customer records for this period.</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section class="limitations-grid" aria-label="Customer analytics limitations">
                <article><i class="material-icons" aria-hidden="true">campaign</i><div><strong>Campaign revenue is not attributed yet</strong><p>{{ campaignActivity.grants || 0 }} local point grants are visible, but no trusted campaign-to-sale revenue join exists.</p></div></article>
                <article><i class="material-icons" aria-hidden="true">history</i><div><strong>Winback lists are not published yet</strong><p>A trusted, consent-aware lapsed-customer query is not part of the current measurement layer.</p></div></article>
            </section>

            <p v-if="source.refreshedAt" class="refresh-note">Live from the API and reporting database · {{ dateRange.startDate }} to {{ dateRange.endDate }} · refreshed {{ formatTimestamp(source.refreshedAt) }}</p>
        </template>

        <AnalyticsGoalPanel :is-dark="isDark" active-goal-id="customer-loyalty" />
    </section>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AnalyticsGoalPanel from "../../components/analytics/AnalyticsGoalPanel.vue"
import EChartsHorizontalBarChart from "../../components/charts/EChartsHorizontalBarChart.vue"
import EChartsRetentionHeatmap from "../../components/charts/EChartsRetentionHeatmap.vue"
import EChartsStackedBarChart from "../../components/charts/EChartsStackedBarChart.vue"
import { getBusinessDateRangeForPreset } from "../../utils/business-date"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import { http } from "../../utils/http"

const integerFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 })
const currencyFormatter = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" })
const percentFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 1 })
const dateFormatter = new Intl.DateTimeFormat("en-CA", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })

export default {
    name: "CustomerLoyaltyDashboard",
    components: { AnalyticsGoalPanel, EChartsHorizontalBarChart, EChartsRetentionHeatmap, EChartsStackedBarChart },
    data() {
        const range = getBusinessDateRangeForPreset("last30days")
        return {
            loading: false,
            error: "",
            warehouseId: "",
            period: "last30days",
            interval: "week",
            includePilotSales: false,
            dateRange: { startDate: range.start, endDate: range.end },
            summary: {},
            customerMixTrend: [],
            valueBands: [],
            frequencyBands: [],
            engagementFunnel: [],
            cohortRetention: { periods: [], cohorts: [], customerCount: 0 },
            campaignActivity: {},
            topCustomers: [],
            context: {},
            source: {},
            requestSequence: 0,
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"]),
        ...mapState("auth", ["user"]),
        isDark() { return this.theme === this.THEMES.DARK },
        canViewDashboard() {
            return this.hasPermission(PERMISSION_CODES.CMS_VIEW_REPORT_CUSTOMER)
                && this.hasPermission(PERMISSION_CODES.CMS_VIEW_REPORT_SALES_REPORT)
                && this.hasPermission(PERMISSION_CODES.CMS_VIEW_LOYALTY)
        },
        summaryCards() {
            return [
                { label: "Identified customers", value: this.formatInteger(this.summary.identifiedCustomers), helper: `${this.formatInteger(this.summary.identifiedOrders)} of ${this.formatInteger(this.summary.totalOrders)} paid orders`, icon: "groups", tone: "neutral" },
                { label: "Repeat purchase rate", value: this.formatPercent(this.summary.repeatPurchaseRate), helper: `${this.formatInteger(this.summary.repeatCustomers)} customers with 2+ orders`, icon: "repeat", tone: this.summary.repeatPurchaseRate ? "success" : "warning" },
                { label: "Purchase frequency", value: `${Number(this.summary.purchaseFrequency || 0).toFixed(2)}×`, helper: "Orders per identified customer", icon: "shopping_bag", tone: "neutral" },
                { label: "Marketing opt-ins", value: this.formatInteger(this.summary.marketingOptInCustomers), helper: `${this.formatPercent(this.summary.marketingOptInRate)} of identified customers`, icon: "mark_email_read", tone: "neutral" },
                { label: "Loyalty enrolled", value: this.formatInteger(this.summary.loyaltyEnrolledCustomers), helper: `${this.formatPercent(this.summary.loyaltyEnrollmentRate)} of identified customers`, icon: "loyalty", tone: this.summary.loyaltyEnrolledCustomers ? "success" : "warning" },
                { label: "Identified revenue", value: this.formatCurrency(this.summary.identifiedRevenue), helper: `${this.formatInteger(this.summary.anonymousOrders)} anonymous orders excluded`, icon: "payments", tone: "neutral" },
            ]
        },
        mixLabels() { return this.customerMixTrend.map(item => this.formatShortDate(item.period)) },
        mixSeries() {
            return [
                { name: "New customers", values: this.customerMixTrend.map(item => item.newCustomers), color: "#347a63" },
                { name: "Returning customers", values: this.customerMixTrend.map(item => item.returningCustomers), color: "#c9964a" },
            ]
        },
    },
    mounted() {
        this.setData({ pageTitle: "Customer & Loyalty" })
        if (this.canViewDashboard) this.fetchDashboard()
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        hasPermission(permissionId) {
            return checkHasPermission({ isAdmin: Boolean(this.user?.isAdmin), permissionId, userPermissionIds: this.user?.permissionIds || [] })
        },
        changePeriod() {
            const range = getBusinessDateRangeForPreset(this.period)
            this.dateRange = { startDate: range.start, endDate: range.end }
            this.fetchDashboard()
        },
        async fetchDashboard() {
            if (!this.canViewDashboard) return
            const requestId = ++this.requestSequence
            this.loading = true
            this.error = ""
            try {
                const response = await http.get("/analytics/customer-loyalty", { params: {
                    startDate: this.dateRange.startDate,
                    endDate: this.dateRange.endDate,
                    interval: this.interval,
                    warehouse_id: this.warehouseId || undefined,
                    includePilotSales: this.includePilotSales || undefined,
                } })
                if (requestId !== this.requestSequence) return
                const result = response.data.result || {}
                this.summary = result.summary || {}
                this.customerMixTrend = result.customerMixTrend || []
                this.valueBands = result.valueBands || []
                this.frequencyBands = result.frequencyBands || []
                this.engagementFunnel = result.engagementFunnel || []
                this.cohortRetention = result.cohortRetention || { periods: [], cohorts: [], customerCount: 0 }
                this.campaignActivity = result.campaignActivity || {}
                this.topCustomers = result.topCustomers || []
                this.context = result.context || {}
                this.source = result.source || {}
            } catch (error) {
                if (requestId !== this.requestSequence) return
                this.error = error.response?.data?.message || "The customer and loyalty source returned an error."
            } finally {
                if (requestId === this.requestSequence) this.loading = false
            }
        },
        formatInteger(value) { return integerFormatter.format(Number(value) || 0) },
        formatCurrency(value) { return currencyFormatter.format(Number(value) || 0) },
        formatPercent(value) { return `${percentFormatter.format(Number(value) || 0)}%` },
        formatShortDate(value) { return value ? dateFormatter.format(new Date(`${value}T00:00:00.000Z`)) : "—" },
        formatTimestamp(value) { return new Intl.DateTimeFormat("en-CA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) },
    },
}
</script>

<style lang="scss" scoped>
@use "@/assets/variables.scss" as *;

.customer-loyalty-dashboard { padding: $cardPaddingY; color: $textColor; }
.dashboard-header, .section-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; }
.dashboard-header { margin-bottom: 18px; h1 { color: $primaryColor; font-size: 28px; margin: 0; } }
.subtitle, .section-heading p, .coverage-banner p, .limitations-grid p { color: rgba($textColor, .66); margin: 5px 0 0; }
.eyebrow { color: rgba($textColor, .55); font-size: 11px; font-weight: 800; margin: 0 0 5px; text-transform: uppercase; }
.header-link, .mini-link, .row-link { color: $primaryColor; font-weight: 700; text-decoration: none; }
.header-link { align-items: center; display: inline-flex; gap: 6px; white-space: nowrap; }
.control-bar { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 8px; display: flex; gap: 14px; margin-bottom: 18px; padding: 14px; }
.control-bar label { display: flex; flex: 1; flex-direction: column; gap: 5px; min-width: 150px; span { font-size: 11px; font-weight: 800; text-transform: uppercase; } }
.control-bar select { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 6px; color: $textColor; min-height: 38px; padding: 0 10px; }
.control-bar select:focus-visible, button:focus-visible, a:focus-visible { box-shadow: 0 0 0 3px rgba($primaryColor, .14); outline: 2px solid $primaryColor; outline-offset: 2px; }
.control-bar .pilot-toggle { align-items: center; flex-direction: row; justify-content: flex-end; span { text-transform: none; } }
.state-panel, .coverage-banner { align-items: center; border: 1px solid $lightBorderColor; border-radius: 8px; display: flex; gap: 14px; margin-bottom: 18px; padding: 18px; }
.state-panel h2, .coverage-banner strong { color: $primaryColor; font-size: 16px; margin: 0; }
.state-panel p { margin: 4px 0 0; }
.loading-state i { animation: spin 1.1s linear infinite; }
.error-state { border-color: rgba($dangerColor, .35); }
.coverage-banner.warning { background: rgba($warningColor, .08); border-color: rgba($warningColor, .3); }
.coverage-banner i { color: $warningColor; }
.metric-grid { display: grid; gap: 12px; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-bottom: 18px; }
.metric-card { align-items: center; background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 8px; display: flex; gap: 13px; min-height: 112px; padding: 15px; }
.metric-card > i { align-items: center; background: rgba($primaryColor, .08); border-radius: 50%; color: $primaryColor; display: flex; height: 42px; justify-content: center; width: 42px; }
.metric-card span, .metric-card small { color: rgba($textColor, .64); display: block; }
.metric-card strong { color: $primaryColor; display: block; font-size: 24px; margin: 3px 0; }
.metric-card.success { border-top: 3px solid $successColor; } .metric-card.warning { border-top: 3px solid $warningColor; }
.chart-grid { display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 18px; }
.dashboard-card { background: $lightCardBg; border: 1px solid $lightBorderColor; border-radius: 8px; box-shadow: $cardShadow; padding: 17px; }
.chart-card { min-height: 390px; } .chart-card.wide { grid-column: 1 / -1; }
.chart-frame { height: 300px; min-height: 300px; width: 100%; }
.trend-chart-frame { height: 360px; min-height: 360px; }
.retention-chart-frame { height: 340px; min-height: 340px; }
.section-heading { margin-bottom: 12px; h2 { color: $primaryColor; font-size: 18px; margin: 0; } p { font-size: 12px; } }
.source-badge, .status-pill { border-radius: 999px; font-size: 10px; font-weight: 800; padding: 5px 8px; text-transform: uppercase; white-space: nowrap; }
.source-badge { background: rgba($primaryColor, .08); color: $primaryColor; }
.empty-chart, .empty-row { color: rgba($textColor, .58); padding: 48px 12px; text-align: center; }
.definition-card dl { margin: 0; } .definition-card dl div { border-top: 1px solid $lightBorderColor; padding: 13px 0; } .definition-card dt { color: $primaryColor; font-weight: 800; } .definition-card dd { color: rgba($textColor, .68); margin: 5px 0 0; }
.table-card { margin-bottom: 18px; }
.table-scroll { overflow-x: auto; }
table { border-collapse: collapse; width: 100%; } th, td { border-bottom: 1px solid $lightBorderColor; padding: 11px 9px; text-align: left; vertical-align: middle; } th { color: rgba($textColor, .58); font-size: 11px; text-transform: uppercase; } td small { color: rgba($textColor, .55); display: block; font-size: 10px; margin-top: 3px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status-pill.success { background: rgba($successColor, .12); color: darken($successColor, 16%); } .status-pill.muted { background: rgba($textColor, .07); color: rgba($textColor, .58); }
.limitations-grid { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 18px; }
.limitations-grid article { align-items: flex-start; background: rgba($warningColor, .06); border: 1px solid rgba($warningColor, .22); border-radius: 8px; display: flex; gap: 12px; padding: 15px; } .limitations-grid strong { color: $primaryColor; } .limitations-grid i { color: $warningColor; }
.refresh-note { color: rgba($textColor, .56); font-size: 11px; text-align: right; }
.dark { color: $darkTextColor; .dashboard-header h1, .section-heading h2, .metric-card strong, .state-panel h2, .coverage-banner strong, .header-link, .mini-link, .row-link, .limitations-grid strong, .definition-card dt { color: $secondaryColorDark; } .subtitle, .section-heading p, .coverage-banner p, .limitations-grid p, .metric-card span, .metric-card small, .definition-card dd { color: rgba($darkTextColor, .7); } .control-bar, .metric-card, .dashboard-card { background: $darkCardBg; border-color: $darkBorderColor; } .control-bar select { background: $darkInputBg; border-color: $darkBorderColor; color: $darkTextColor; } th, td, .definition-card dl div { border-color: $darkBorderColor; } th, td small, .empty-chart, .empty-row, .refresh-note { color: rgba($darkTextColor, .6); } }
@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .loading-state i { animation: none; } }
@media (max-width: 900px) { .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .control-bar { flex-wrap: wrap; } .control-bar .pilot-toggle { justify-content: flex-start; } }
@media (max-width: 680px) { .customer-loyalty-dashboard { padding: 12px; } .dashboard-header, .section-heading { flex-direction: column; } .metric-grid, .chart-grid, .limitations-grid { grid-template-columns: 1fr; } .chart-card.wide { grid-column: auto; } .control-bar { flex-direction: column; } .metric-card { min-height: 96px; } }
</style>
